import { outranks, type WorkspaceRole } from "./roles.js";

export const WORKSPACE_PERMISSIONS = [
  "workspace.view",
  "workspace.update",
  "workspace.delete",
  "workspace.transferOwnership",
  "members.view",
  "members.invite",
  "members.updateRole",
  "members.remove",
  "teams.viewAll",
  "teams.create",
  "teams.manage",
  "teams.manageOwn",
  "teams.delete",
  "projects.viewAll",
  "projects.create",
  "projects.manage",
  "projects.manageOwn",
  "projects.delete",
  "tasks.create",
  "tasks.update",
  "tasks.delete",
  "tags.manage",
  "comments.moderate",
  "channels.create",
] as const;
export type WorkspacePermission = (typeof WORKSPACE_PERMISSIONS)[number];

const ALL: readonly WorkspaceRole[] = ["OWNER", "ADMIN", "MANAGER", "MEMBER", "GUEST"];
const ADMINS: readonly WorkspaceRole[] = ["OWNER", "ADMIN"];
const MANAGERS: readonly WorkspaceRole[] = ["OWNER", "ADMIN", "MANAGER"];

/**
 * Single source of truth for role → permission mapping.
 * The API enforces it; the web app uses it only to hide unavailable actions.
 */
export const WORKSPACE_PERMISSION_ROLES: Readonly<
  Record<WorkspacePermission, readonly WorkspaceRole[]>
> = {
  "workspace.view": ALL,
  "workspace.update": ADMINS,
  "workspace.delete": ["OWNER"],
  "workspace.transferOwnership": ["OWNER"],
  "members.view": ALL,
  "members.invite": ADMINS,
  "members.updateRole": ADMINS,
  "members.remove": ADMINS,
  "teams.viewAll": ["OWNER", "ADMIN", "MANAGER", "MEMBER"],
  "teams.create": MANAGERS,
  "teams.manage": ADMINS,
  "teams.manageOwn": MANAGERS,
  "teams.delete": ADMINS,
  // Everyone else (managers, members, guests) only sees the projects they belong to.
  "projects.viewAll": ADMINS,
  "projects.create": MANAGERS,
  "projects.manage": ADMINS,
  "projects.manageOwn": MANAGERS,
  "projects.delete": ADMINS,
  // Guests are read-only on tasks.
  "tasks.create": ["OWNER", "ADMIN", "MANAGER", "MEMBER"],
  "tasks.update": ["OWNER", "ADMIN", "MANAGER", "MEMBER"],
  "tasks.delete": MANAGERS,
  // Workspace-wide tag definitions; any member who can edit a task may apply them.
  "tags.manage": ADMINS,
  // Deleting other people's comments.
  "comments.moderate": ADMINS,
  // Guests can read and write in channels they are put in, but not start new ones.
  "channels.create": ["OWNER", "ADMIN", "MANAGER", "MEMBER"],
};

export function hasWorkspacePermission(
  role: WorkspaceRole,
  permission: WorkspacePermission,
): boolean {
  return WORKSPACE_PERMISSION_ROLES[permission].includes(role);
}

/**
 * Whether `actor` may grant `nextRole` to a member whose current role is `currentRole`
 * (`null` for a new invitation). OWNER can only change hands through ownership transfer.
 */
export function canAssignRole(
  actor: WorkspaceRole,
  currentRole: WorkspaceRole | null,
  nextRole: WorkspaceRole,
): boolean {
  if (nextRole === "OWNER" || currentRole === "OWNER") return false;
  if (
    !hasWorkspacePermission(actor, currentRole === null ? "members.invite" : "members.updateRole")
  ) {
    return false;
  }
  if (!outranks(actor, nextRole)) return false;
  return currentRole === null || outranks(actor, currentRole);
}

/** Whether `actor` may remove a member with `targetRole` (leaving your own workspace is handled separately). */
export function canRemoveMember(actor: WorkspaceRole, targetRole: WorkspaceRole): boolean {
  return (
    targetRole !== "OWNER" &&
    hasWorkspacePermission(actor, "members.remove") &&
    outranks(actor, targetRole)
  );
}

/** Team-level management: admins manage every team; managers manage the teams they lead. */
export function canManageTeam(role: WorkspaceRole, isTeamLead: boolean): boolean {
  return (
    hasWorkspacePermission(role, "teams.manage") ||
    (isTeamLead && hasWorkspacePermission(role, "teams.manageOwn"))
  );
}

/** Project-level management: admins manage every project; managers manage the projects they own. */
export function canManageProject(role: WorkspaceRole, isOwner: boolean): boolean {
  return (
    hasWorkspacePermission(role, "projects.manage") ||
    (isOwner && hasWorkspacePermission(role, "projects.manageOwn"))
  );
}
